import React, { useContext } from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { ReferralType, BiEventType } from '@wix/da-bi/pkg/constants';
import { BiData, SubmitButtonClickBiEvent } from '@wix/da-bi/pkg/events';
import CoreBadge from '@wix/da-ds/pkg/Badges/CoreBadge';
import { URLS } from '@wix/da-url';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import SophisticatedTooltipLink from '@wix/da-ds/pkg/tooltips/SophisticatedTooltipLink';
import SophisticatedTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SophisticatedTooltip';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { PapiUser } from '@wix/da-papi-types';

import EmptyState from '../../../../EmptyState';
import CoreExclusive from '../CoreExclusive';

import s from './PremiumFoldersEmptyState.scss';

export interface Props {
  view: 'wide' | 'large' | 'small';
  user: PapiUser;
  isOwner: boolean;
  onOpenChat: (user: PapiUser) => void;
  hasCoreMembership?: boolean;
}

export const PremiumFoldersEmptyState: React.FC<Props> = ({
  view,
  user,
  user: { username },
  isOwner,
  onOpenChat,
  hasCoreMembership,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const isUplift = useFlag('profile_cta_uplift');
  const isPaywallUpdates = useFlag('paywall_updates');

  if (isOwner) {
    if (hasCoreMembership) {
      return isPaywallUpdates ? (
        <EmptyState
          className={classnames(s['root'], s[view])}
          title={
            <>
              {isMobile && (
                <div className={s['before-title']}>
                  {t('pages.shop.emptyState.desktopOnly')}
                </div>
              )}
              {t('pages.shop.emptyState.core.premiumFolder.title')}
            </>
          }
          subtitle={t('pages.shop.emptyState.core.premiumFolder.subtitle')}
          subtitleClassName={s['subtitle']}
          illustration={<CoreExclusive />}
          illustrationClassName={s['exclusive']}
          withButton={isUplift || !isMobile}
          buttonLabel={t('common.submit')}
          buttonVariant={isUplift ? 'approval-green' : undefined}
          onButtonClick={() => window.location.replace(URLS.SUBMIT)}
          biData={BiData<SubmitButtonClickBiEvent>({
            evid: 704,
            sectionname: 'premiumFolders',
          })}
          view={'small'}
        />
      ) : (
        <EmptyState
          className={classnames(s['root'])}
          title={
            <>
              <SophisticatedTooltip
                size="medium"
                aria-label={t(
                  'pages.shop.emptyState.nonCore.premiumFolder.tooltip'
                )}
                description={t(
                  'pages.shop.emptyState.nonCore.premiumFolder.tooltip'
                )}
                postfix={
                  <SophisticatedTooltipLink href="https://www.deviantartsupport.com/en/article/what-is-a-core-membership">
                    {t('common.learnMore')}
                  </SophisticatedTooltipLink>
                }
              >
                <CoreBadge size="large" />
              </SophisticatedTooltip>{' '}
              {t('pages.shop.emptyState.core.premiumFolder.title')}
            </>
          }
          subtitle={t('pages.shop.emptyState.core.premiumFolder.subtitle')}
          subtitleClassName={s['subtitle']}
          withButton={isUplift || !isMobile}
          buttonLabel={t('common.submit')}
          buttonVariant={isUplift ? 'approval-green' : undefined}
          onButtonClick={() => window.location.replace(URLS.SUBMIT)}
          biData={BiData<SubmitButtonClickBiEvent>({
            evid: 704,
            sectionname: 'premiumFolders',
          })}
        />
      );
    } else {
      // paywall_updates: clean up translation key as well
      return isPaywallUpdates ? (
        <EmptyState
          className={classnames(s['root'], s[view])}
          title={
            <>
              {isMobile && (
                <div className={s['before-title']}>
                  {t('pages.shop.emptyState.desktopOnly')}
                </div>
              )}
              {t('pages.shop.emptyState.nonCore.premiumFolder.title')}
            </>
          }
          subtitle={
            <Trans i18nKey="pages.shop.emptyState.nonCore.premiumFolder.subtitle__paywall_updates">
              Let deviants pay you to unlock your exclusive content. Add
              tutorials, works in progress, resources, and more.
            </Trans>
          }
          subtitleClassName={s['subtitle__paywall_updates']}
          illustration={<CoreExclusive />}
          illustrationClassName={s['exclusive']}
          withButton={true}
          customButton={
            <BrandedButton
              size="medium"
              variant="core-orange"
              href={URLS.CORE_MEMBERSHIP}
            >
              {t('widgets.tierContent.empty.cta.getCore')}
            </BrandedButton>
          }
          view={'small'}
        />
      ) : (
        <EmptyState
          className={classnames(s['root'])}
          title={t('pages.shop.emptyState.nonCore.premiumFolder.title')}
          subtitle={
            <Trans i18nKey="pages.shop.emptyState.nonCore.premiumFolder.subtitle">
              <BiLink
                href={URLS.CORE_MEMBERSHIP}
                className={s['upgrade-to-core']}
                biData={{
                  evid: BiEventType.BUY_CORE_BUTTON,
                  referral_info: ReferralType.PAYWALL_PG,
                }}
              >
                Upgrade to Core{' '}
              </BiLink>
              <SophisticatedTooltip
                size="medium"
                className={s['core-icon']}
                aria-label={t(
                  'pages.shop.emptyState.nonCore.premiumFolder.tooltip'
                )}
                description={t(
                  'pages.shop.emptyState.nonCore.premiumFolder.tooltip'
                )}
                postfix={
                  <SophisticatedTooltipLink href="https://www.deviantartsupport.com/en/article/what-is-a-core-membership">
                    {t('common.learnMore')}
                  </SophisticatedTooltipLink>
                }
              >
                <CoreBadge size="large" />
              </SophisticatedTooltip>
              to let deviants pay you to unlock your exclusive content.
              <br />
              Add tutorials, works in progress, resources, and more.
            </Trans>
          }
          subtitleClassName={s['subtitle']}
        />
      );
    }
  }

  const handleOpenChat = () => onOpenChat(user);

  return (
    <EmptyState
      className={classnames(s['root'], s[view])}
      title={t('pages.shop.emptyState.premiumFolders.title.visitor', {
        username,
      })}
      subtitle={t('pages.shop.emptyState.premiumFolders.subtitle.visitor')}
      subtitleClassName={s['subtitle']}
      onButtonClick={handleOpenChat}
      buttonLabel={t('pages.shop.emptyState.openChatWithArtist')}
      withButton
    />
  );
};
PremiumFoldersEmptyState.displayName = 'PremiumFoldersEmptyState';

export default PremiumFoldersEmptyState;
